import Messenger from '@/Utility/Messenger';
import Constants from '@/AppConstants';
import ComponentManager from '@/Management/ComponentManager';
import NavigationService from '@/Management/NavigationService';
import NavigationHelper from '@/Management/NavigationHelper';
import RADApplication from '@/Management/RADApplication';
import Address from '@/Model/Address';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import Loader from '@/Utility/Loader';
import Utilities from '@/Utility/Utilities';

export default class FrontController {

    /**
     * Enthält eine Liste von Operations-Aufrufen 
     */
    private static calls = {};

    /**
     * Entfernt einen Call aus der Liste 
     */
    public static removeCall(id: string): void {
        if (id in FrontController.calls) {
            delete FrontController.calls[id];
        }
    }

    /**
     * Liefert einen Call anhand der Id oder Null
     */
    public static getCall(id: string): any {
        if (id in FrontController.calls) {
            return FrontController.calls[id];
        }

        return null;
    }

    /**
     * Erzeugt einen neuen Operation-Call und ruft diesen mit den übergebenen Parametern auf
     */
    public static managedCallStaticOperation(pdTypeName: string, operationName: string, operationParameters: any, contextParameters: any, onSuccess?: (self: ManagedStaticOperationCall, operationResultDTO: any) => void, onError?: (self: FrontController, operationResultDTO: any) => void, resetNavigation?: boolean) {
        let managedStaticOperationCall = new ManagedStaticOperationCall(pdTypeName, operationName, contextParameters, onSuccess, onError);
        managedStaticOperationCall.resetNavigation = resetNavigation;

        FrontController.calls[managedStaticOperationCall.id] = managedStaticOperationCall;
        managedStaticOperationCall.call(operationParameters);
    }

    /**
     * Initialisiert den FrontController
     * z.B. Messages werden registriert
     */
    public static init(): void {
        Messenger.registerForMessageId('DetailViewExitedMessage', (message) => {
            if (message.context != null && message.context._callId in FrontController.calls) {
                FrontController.calls[message.context._callId].onDetailViewClosed(message);
            }
        }, FrontController);
    }

    public static evaluateDetailViewContextAfterCallOperation(context, result): void {
        let parameter = context.getContextParameters();
        if (parameter.hasOwnProperty(Constants.CALL_ENVELOPE_DV_FORCE_CLOSING_ACTION)) {
            let value = parameter[Constants.CALL_ENVELOPE_DV_FORCE_CLOSING_ACTION];
            //parameter.remove(Constants.CALL_ENVELOPE_DV_FORCE_CLOSING_ACTION);

            if (value === 'SAVE') {
                context.save();
            }
            else if (value === 'CANCEL') {
                context.cancel();
            }
        }
        else if (parameter.hasOwnProperty(Constants.CALL_ENVELOPE_DV_FORCE_APPLY_ACTION)) {
            let value = parameter[Constants.CALL_ENVELOPE_DV_FORCE_APPLY_ACTION];
            if (value === 'APPLY') {
                context.apply();
            }
        }
    }
} // end of class FrontController


class ManagedStaticOperationCall {

    private MessageBoxViewModel;

    /**
     * Die Id des Operation-Calls
     * z.B.: $$OPERATION_NAME$$_123
     */
    public id;

    /**
     * Der Typ auf dem die Operation aufgerufen werden soll
     */
    public pdTypeName;

    /**
     * Der Name der Operation
     */
    public operationName;

    public contextNeedsRefresh;

    /**
     * Kontext-Parameter
     */
    public contextParameters;

    /**
     * Nach der Navigation erfolgt ein Aufruf von ResetNaviagtion
     */
    public resetNavigation;

    /**
     * Der Callback nach einem erfolgreichen Aufruf der Operation
     * Optional
     */
    public onSuccess;

    /**
     * Der Callback nach einem fehlerhaften Aufruf der Operation
     * Optional
     */
    public onError;

    public isClosed;

    constructor(pdTypeName, operationName, contextParameters, onSuccess, onError) {
        this.MessageBoxViewModel = ComponentManager.getViewModel('MessageBoxViewModel');
        this.id = _.uniqueId(operationName + '_');
        this.pdTypeName = pdTypeName;
        this.operationName = operationName;
        this.contextNeedsRefresh = true;
        this.contextParameters = contextParameters || {};
        this.onSuccess = onSuccess || this.onDefaultSuccess.bind(this);
        this.onError = onError || this.onDefaultError.bind(this);

        this.isClosed = false;
    }

    public evalContextNeedsRefresh() {
        if (!!this.contextParameters && _.has(this.contextParameters, Constants.CALL_ENVELOPE_CONTEXT_NEEDS_REFRESH)) {
            let sbool = this.contextParameters[Constants.CALL_ENVELOPE_CONTEXT_NEEDS_REFRESH];
            this.contextNeedsRefresh = sbool.toLowerCase() == true.toString().toLowerCase();
        }
    }

    /*
     * Der Default-Callback nach einem erfolgreichen Aufruf der Operation
     */
    public onDefaultSuccess(managedStaticOperationCall, operationResultDTO) {
        let action = this.contextParameters[Constants.CALL_ENVELOPE_CLIENT_ACTION_STRING];
        let pdoId = this.contextParameters[Constants.CALL_ENVELOPE_SELECT_OBJECT_IN_LV_ACTION_STRING];
        let url = this.contextParameters[Constants.CALL_ENVELOPE_OPEN_WEBPAGE];
        let openInNewWindow = (Utilities.isNullOrUndefined(this.contextParameters['openInNewWindow']) ? false :
            (this.contextParameters['openInNewWindow'] + '').toLowerCase() === 'true');

        if (action != null) {
            // wozu war diese Ersetzungen?
            // verursacht Probleme, wenn z.B. in der action ein Filter-String der Form this.PDO_ID IN (...) vorkommt
            // action = action.replace(/\s/g, '');

            // Die vom Server erzeugten URLs enthalten die Schreibung PdoID statt PdoID; in diesem Client wird (in den Properties) aber PdoId benutzt.
            action = action.replace('PdoID=', 'PdoId=');

            let address = new Address(action);
            address.setParameter('ContextID', this.id);

            if (this.resetNavigation) {
                address.onNavigated(() => NavigationService.getInstance().notifyNavigationReset());
            }

            if (openInNewWindow)
                NavigationService.getInstance().navigateToAddress(address, null, null, null, openInNewWindow);
            else
                NavigationService.getInstance().navigateToAddress(address);
        } else if (pdoId != null) {
            let lv = RADApplication.getInstance().currentListViewContext();
            if (lv != null && lv.dataGrid != null) {
                lv.dataGrid.selected([pdoId]);
                lv.refreshPersistenceContext();
            }
        } else if (url != null) {
            NavigationHelper.openWebPage(url, '_blank');
            this.close();
        } else {
            this.close();
        }
    }

    /**
     * Der Default-Callback nach einem fehlerhaften Aufruf der Operation
     */
    public onDefaultError(managedStaticOperationCall, operationResultDTO) {
        RADApplication.getInstance().showMessageBox(this.MessageBoxViewModel.createErrorMessageBox(operationResultDTO.errorMessage));
        this.close();
    }

    /**
     * Wird aufgerufen sobald die DetailView verlassen wird
     */
    public onDetailViewClosed(message) {
        let action = this.contextParameters[Constants.CALL_ENVELOPE_DV_ACTION_CLOSE];

        if (action != null) {
            delete this.contextParameters[Constants.CALL_ENVELOPE_DV_ACTION_CLOSE];

            let address = new Address(action);
            let name = address.getName();

            if (name === 'CallStaticOperation') {
                this.contextParameters[Constants.CALL_ENVELOPE_DV_HAS_OBJECT_CHANGED] = message.hasObjectChanged;
                this.contextParameters[Constants.CALL_ENVELOPE_DV_LEAVING_ACTION] = message.leavingAction;
                this.contextParameters[Constants.CALL_ENVELOPE_DV_HAS_PENDING_DVC] = message.hasPendingDVC;

                let pdTypeName = address.getParameter('TypeName') as string;
                let operationName = address.getParameter('OperationName') as string;

                if (pdTypeName != null && operationName != null) {
                    Loader.getInstance().show();

                    RADEnvironmentProxy.getInstance().callStaticOperation({
                        pdTypeName: pdTypeName,
                        operationName: operationName,
                        operationParameters: null,
                        contextParameters: this.contextParameters
                    }).then((operationResultDTO) => {
                        this.contextParameters = operationResultDTO.contextParameters;

                        if (operationResultDTO.status == 'Successfull') {
                            if (operationResultDTO.contextParameters[Constants.CALL_ENVELOPE_SHOW_RESULT_IN_GUI] === 'true') {
                                RADApplication.getInstance().showMessageBox(this.MessageBoxViewModel.createNoticeMessageBox(operationResultDTO.returnValue + ''));
                            }

                            this.onSuccess(this, operationResultDTO);
                        } else {
                            this.onError(this, operationResultDTO);
                        }
                    }).always(() => {
                        Loader.getInstance().hide();
                    });
                }
            }
        }
    }


    /*
     * Ruft die Operation über den RADEnvironmentProxy auf
     */
    public call(operationParameters) {
        Loader.getInstance().show();

        RADEnvironmentProxy.getInstance().callStaticOperation({
            pdTypeName: this.pdTypeName,
            operationName: this.operationName,
            operationParameters: operationParameters,
            contextParameters: this.contextParameters
        }).then((operationResultDTO) => {
            this.contextParameters = $.extend({}, this.contextParameters, operationResultDTO.contextParameters);

            if (operationResultDTO.status == 'Successfull') {
                if (operationResultDTO.contextParameters[Constants.CALL_ENVELOPE_SHOW_RESULT_IN_GUI] === 'true') {
                    RADApplication.getInstance().showMessageBox(this.MessageBoxViewModel.createNoticeMessageBox(operationResultDTO.returnValue + ''));
                }

                this.onSuccess(this, operationResultDTO);
            }
            else {
                this.onError(this, operationResultDTO);
            }
        }).always(() => {
            Loader.getInstance().hide();
        });
    }

    public close() {
        this.evalContextNeedsRefresh();

        FrontController.removeCall(this.id);
        this.isClosed = true;
    }
}